import { ReactComponent as LineNumbersIcon } from 'assets/icons/listNumbered16.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash16.svg'
import cx from 'classnames'
import type CM from 'codemirror'
import type { Editor as CMEditor } from 'codemirror'
import { DEFAULT_MONOSPACE_FONT } from 'common/Prose/constants'
import { InlineFormatBar } from 'components/InlineFormatBar/InlineFormatBar'
import { InlineFormatButton } from 'components/InlineFormatBar/components/InlineFormatButton/InlineFormatButton'
import { InlineFormatDivider } from 'components/InlineFormatBar/components/InlineFormatDivider/InlineFormatDivider'
import { startCase } from 'lodash'
import { nodes, schema, ExtractStrongNodeSpecAttributes } from 'prose-core'
import { exitCode } from 'prosemirror-commands'
import { redo, undo } from 'prosemirror-history'
import { Node } from 'prosemirror-model'
import { Selection, TextSelection } from 'prosemirror-state'
import { Decoration, EditorView, NodeView } from 'prosemirror-view'
import { useState } from 'react'
import { reportError } from 'utils/error'
import Styles from './Code.module.scss'
import { SearchDropdown } from './SearchDropdown'
import { CODE_LANGUAGES } from './constants/codeLanguages'
import { CODE_THEMES } from './constants/codeThemes'
import { languageKeyFromMimeMode } from './utils/languageKeyFromMimeMode'
import { createRoot, Root } from 'react-dom/client'

const LoadCodeMirror = () => import('common/Prose/nodeViews/Code/LazyCodeMirror')

type code_block_attrs = ExtractStrongNodeSpecAttributes<typeof nodes.code_block>

interface HoverContainerProps {
  handleToggleLineNumbers: () => void
  handleDelete: () => void
  handleLanguageSelection: (language: keyof typeof CODE_LANGUAGES) => void
  currentLanguage: string
  handleThemeSelection: (theme: (typeof CODE_THEMES)[number]) => void
  currentTheme: (typeof CODE_THEMES)[number]
}

const renderThemeItem = (theme: (typeof CODE_THEMES)[number]) => {
  return (
    <div className={Styles.themeItemContainer}>
      <div className={Styles.themeItemLabel}>{startCase(theme)}</div>
      <div className={cx(Styles.themePreview, Styles[`theme-${theme}`])}>
        <span className={Styles.tag}>{'<b>'}</span>
        <span className={Styles.code}>code</span>
        <span className={Styles.tag}>{'</b>'}</span>
      </div>
    </div>
  )
}

const HoverContainer = ({
  handleDelete,
  handleToggleLineNumbers,
  handleLanguageSelection,
  currentLanguage,
  handleThemeSelection,
  currentTheme,
}: HoverContainerProps) => {
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false)
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false)
  return (
    <InlineFormatBar
      className={cx(Styles.InlineFormatBar, { [Styles.forceVisible]: isLanguageDropdownOpen || isThemeDropdownOpen })}
    >
      <SearchDropdown<[string, (typeof CODE_LANGUAGES)[string]]>
        tooltipLabel="Select a language"
        options={Object.entries(CODE_LANGUAGES)}
        currentSelection={[currentLanguage, CODE_LANGUAGES[currentLanguage]]}
        handleSelection={([key]) => handleLanguageSelection(key)}
        itemValue={([key]) => key}
        itemLabel={([, { textValue }]) => textValue}
        filterLogic={(lowerCaseSearchTerm) =>
          ([, { textValue }]) =>
            textValue.toLowerCase().includes(lowerCaseSearchTerm)}
        variant="language"
        onOpenStateChange={(isOpen) => setIsLanguageDropdownOpen(isOpen)}
      />
      <InlineFormatDivider />
      <SearchDropdown<(typeof CODE_THEMES)[number]>
        tooltipLabel="Select a theme"
        options={CODE_THEMES}
        currentSelection={currentTheme}
        handleSelection={handleThemeSelection}
        itemValue={(s) => s}
        itemLabel={(s) => startCase(s)}
        itemRender={(s) => renderThemeItem(s)}
        filterLogic={(lowerCaseSearchTerm) => (option: string) => option.toLowerCase().includes(lowerCaseSearchTerm)}
        hideCheckmark={true}
        variant="theme"
        onOpenStateChange={(isOpen) => setIsThemeDropdownOpen(isOpen)}
      />
      <InlineFormatDivider />
      <InlineFormatButton
        onClick={handleToggleLineNumbers}
        label="Toggle line numbers"
        data-testid="Toggle line numbers"
      >
        <LineNumbersIcon />
      </InlineFormatButton>
      <InlineFormatButton onClick={handleDelete} label="Delete element" data-testid="Delete element">
        <TrashIcon />
      </InlineFormatButton>
    </InlineFormatBar>
  )
}

export class Code implements NodeView {
  node: Node
  view: EditorView
  getPos: () => number
  decorations: Decoration[]
  incomingChanges: boolean
  updating: boolean
  readOnly?: boolean
  dom: HTMLElement
  contentDom!: HTMLElement
  hoverContainer!: HTMLElement
  root: Root

  cmClass!: typeof CM
  cm!: CMEditor

  constructor(node: Node, view: EditorView, getPos: () => number, decorations: Decoration[], readOnly?: boolean) {
    this.node = node
    this.view = view
    this.getPos = getPos
    this.decorations = decorations
    this.incomingChanges = false
    this.readOnly = readOnly
    this.updating = false

    // The editor's outer node is our DOM representation
    this.dom = document.createElement('div')
    this.dom.classList.add(Styles.hoverContainer)
    this.dom.dataset.nodeType = this.node.type.name
    this.hoverContainer = document.createElement('div')
    this.root = createRoot(this.hoverContainer)

    LoadCodeMirror().then((cmClass) => {
      this.cmClass = cmClass.CodeMirror as any

      // Create a CodeMirror instance
      //@ts-ignore CodeMirror typically requires an element to insert into, but we pass null in because we just need it to construct itself
      this.cm = new this.cmClass(null, {
        value: this.node.textContent,
        lineNumbers: node.attrs.lineNumbers,
        lineWrapping: node.attrs.lineWrapping,
        indentUnit: node.attrs.indentUnit,
        tabSize: node.attrs.tabSize,
        extraKeys: this.codeMirrorKeymap(),
        theme: node.attrs.theme ?? 'default',
        readOnly,
      })
      this.loadAppropriateMode(node.attrs as code_block_attrs)

      this.contentDom = this.cm.getWrapperElement()
      const fontFamilyMark = node.marks.find(
        (maybeFontFamilyMark) => maybeFontFamilyMark.type === schema.marks.font_family,
      )
      const fontFamily = fontFamilyMark?.attrs?.family ?? DEFAULT_MONOSPACE_FONT
      this.contentDom.style.fontFamily = fontFamily
      this.hoverContainer.classList.add(Styles.floatingMenuWrapper)
      if (!readOnly) {
        this.renderHoverContainer()
        this.dom.appendChild(this.hoverContainer)
      } else {
        this.contentDom.classList.add(Styles.cursorReadonly)
      }
      this.dom.appendChild(this.contentDom)

      // CodeMirror needs to be in the DOM to properly initialize, so
      // schedule it to update itself
      setTimeout(() => this.cm.refresh(), 20)

      // This flag is used to avoid an update loop between the outer and
      // inner editor
      this.updating = false

      // Track whether changes have been made but not yet propagated
      this.cm.on('beforeChange', () => (this.incomingChanges = true))

      // Propagate updates from the code editor to ProseMirror
      this.cm.on('cursorActivity', () => {
        if (!this.updating && !this.incomingChanges) this.forwardSelection()
      })

      this.cm.on('changes', () => {
        if (!this.updating) {
          this.valueChanged()
          this.forwardSelection()
        }
        this.incomingChanges = false
      })

      this.cm.on('focus', this.forwardSelection)
      this.cm.on('blur', this.loseSelection)

      this.update(node)
    })
  }

  private renderHoverContainer = () => {
    if (this.readOnly) return

    const currentLanguage = languageKeyFromMimeMode(this.node.attrs)

    this.root.render(
      <HoverContainer
        handleToggleLineNumbers={this.handleToggleLineNumbers}
        handleDelete={this.handleDelete}
        currentLanguage={currentLanguage}
        currentTheme={this.node.attrs.theme}
        handleLanguageSelection={this.handleLanguageSelection}
        handleThemeSelection={this.handleThemeSelection}
      />,
    )
  }

  private forwardSelection = () => {
    if (!this.cm.hasFocus()) return
    let state = this.view.state
    let selection = this.asProseMirrorSelection(state.doc)
    if (!selection.eq(state.selection)) this.view.dispatch(state.tr.setSelection(selection))
    if (this.hoverContainer && !this.hoverContainer.classList.contains(Styles.activeDropdown)) {
      this.hoverContainer.classList.add(Styles.activeDropdown)
    }
  }

  loseSelection = () => {
    if (this.cm.hasFocus()) return
    if (this.hoverContainer && this.hoverContainer.classList.contains(Styles.activeDropdown)) {
      this.hoverContainer.classList.remove(Styles.activeDropdown)
    }
  }

  asProseMirrorSelection(doc: Node) {
    let offset = this.getPos() + 1
    let anchor = this.cm.indexFromPos(this.cm.getCursor('anchor')) + offset
    let head = this.cm.indexFromPos(this.cm.getCursor('head')) + offset
    return TextSelection.create(doc, anchor, head)
  }

  setSelection(anchor: number, head: number) {
    if (this.cm == null) return
    this.cm.focus()
    this.updating = true
    this.cm.setSelection(this.cm.posFromIndex(anchor), this.cm.posFromIndex(head))
    this.updating = false
  }

  valueChanged() {
    let change = computeChange(this.node.textContent, this.cm.getValue())
    if (change) {
      let start = this.getPos() + 1
      if (change.text) {
        let tr = this.view.state.tr.replaceWith(
          start + change.from,
          start + change.to,
          this.view.state.schema.text(change.text),
        )
        this.view.dispatch(tr)
      } else {
        const tr = this.view.state.tr.delete(start + change.from, start + change.to)
        this.view.dispatch(tr)
      }
    }
  }

  codeMirrorKeymap() {
    let view = this.view
    let mod = /Mac/.test(navigator.platform) ? 'Cmd' : 'Ctrl'
    return this.cmClass.normalizeKeyMap({
      Up: () => this.maybeEscape('line', -1),
      Left: () => this.maybeEscape('char', -1),
      Down: () => this.maybeEscape('line', 1),
      Right: () => this.maybeEscape('char', 1),
      [`${mod}-Z`]: () => undo(view.state, view.dispatch),
      [`Shift-${mod}-Z`]: () => redo(view.state, view.dispatch),
      [`${mod}-Y`]: () => redo(view.state, view.dispatch),
      'Ctrl-Enter': () => {
        if (exitCode(view.state, view.dispatch)) view.focus()
      },
    })
  }

  maybeEscape(unit: string, dir: number) {
    let pos = this.cm.getCursor()
    if (
      this.cm.somethingSelected() ||
      pos.line !== (dir < 0 ? this.cm.firstLine() : this.cm.lastLine()) ||
      (unit === 'char' && pos.ch !== (dir < 0 ? 0 : this.cm.getLine(pos.line).length))
    )
      return this.cmClass.Pass
    this.view.focus()
    let targetPos = this.getPos() + (dir < 0 ? 0 : this.node.nodeSize)
    let selection = Selection.near(this.view.state.doc.resolve(targetPos), dir)
    this.view.dispatch(this.view.state.tr.setSelection(selection).scrollIntoView())
    this.view.focus()
  }

  private loadAppropriateHtmlMode(code_block_attrs: code_block_attrs): void {
    if (code_block_attrs.mode === 'django') {
      this.cm.setOption('mode', 'django')
    } else {
      this.cm.setOption('mode', 'htmlmixed')
    }
  }

  private loadAppropriateMode(code_block_attrs: code_block_attrs): void {
    if (code_block_attrs.mime == null) {
      return this.cm.setOption('mode', undefined)
    }

    if (code_block_attrs.mode === 'text/html') {
      return this.loadAppropriateHtmlMode(code_block_attrs)
    }

    switch (code_block_attrs.mime) {
      case 'text/plain':
        return this.cm.setOption('mode', undefined)
      case 'application/graphql':
        return this.cm.setOption('mode', 'graphql')
      case 'text/x-handlebars-template':
        return this.cm.setOption('mode', 'handlebars')
      case 'text/x-jade':
        return this.cm.setOption('mode', 'pug')
      case 'text/x-wiki':
      case 'text/x-mediawiki':
        return this.cm.setOption('mode', 'slim')
      case 'text/x-pegjs':
        return this.cm.setOption('mode', 'pegjs')
      case 'text/typescript-jsx':
        return this.cm.setOption('mode', 'jsx')
      case 'text/x-sass':
        return this.cm.setOption('mode', 'sass')
      case 'application/dart':
        return this.cm.setOption('mode', 'dart')
      case 'text/x-ebnf':
        return this.cm.setOption('mode', 'ebnf')
      case 'application/x-aspx':
        return this.cm.setOption('mode', 'htmlembedded')
      case 'application/x-ejs':
      case 'application/x-erb':
      case 'text/x-hxml':
      case 'application/x-jsp':
        return this.cm.setOption('mode', 'htmlembedded')
      case 'text/x-haml':
        return this.cm.setOption('mode', 'haml')
      case 'text/x-literate-haskell':
        return this.cm.setOption('mode', 'haxe')
      case 'text/jsx':
        return this.cm.setOption('mode', 'jsx')
      case 'text/x-markdown':
        return this.cm.setOption('mode', 'markdown')
      case 'text/x-php':
        return this.cm.setOption('mode', 'php')
      case 'text/x-pug':
        return this.cm.setOption('mode', 'pug')
      case 'text/x-sas':
        return this.cm.setOption('mode', 'sas')
      case 'application/x-slim':
        return this.cm.setOption('mode', 'slim')
      case 'text/x-smarty':
        return this.cm.setOption('mode', 'smarty')
      case 'text/x-soy':
        return this.cm.setOption('mode', 'soy')
      case 'text/x-tornado':
        return this.cm.setOption('mode', 'tornado')
      case 'text/x-twig':
        return this.cm.setOption('mode', 'twig')
      case 'text/x-vue':
        return this.cm.setOption('mode', 'vue')
      default:
        const modeInfo = this.cmClass.findModeByMIME(code_block_attrs.mime)
        try {
          if (modeInfo == null) {
            return this.cm.setOption('mode', undefined)
          }
          return this.cm.setOption('mode', modeInfo.mime)
        } catch (error) {
          reportError(new Error(`Could not load code language ${code_block_attrs.mime}`), {
            severity: 'error',
            metaData: {
              mode: modeInfo?.mode,
              mime: code_block_attrs.mime,
              modeInfo,
            },
          })
        }
    }
  }

  update(node: Node) {
    if (this.cm == null) return false
    if (node.type !== this.node.type) return false
    this.node = node
    this.renderHoverContainer()
    this.loadAppropriateMode(node.attrs as code_block_attrs)

    // non-strict comparison to catch null and undefined but not false
    // eslint-disable-next-line eqeqeq
    if (node.attrs.lineNumbers != undefined && node.attrs.lineNumbers !== this.cm.getOption('lineNumbers')) {
      this.cm.setOption('lineNumbers', node.attrs.lineNumbers)
    }
    // non-strict comparison to catch null and undefined but not false
    // eslint-disable-next-line eqeqeq
    if (node.attrs.lineWrapping != undefined && node.attrs.lineWrapping !== this.cm.getOption('lineWrapping')) {
      this.cm.setOption('lineWrapping', node.attrs.lineWrapping)
    }
    if (node.attrs.indentUnit && node.attrs.indentUnit !== this.cm.getOption('indentUnit')) {
      this.cm.setOption('indentUnit', node.attrs.indentUnit)
    }
    if (node.attrs.tabSize && node.attrs.tabSize !== this.cm.getOption('tabSize')) {
      this.cm.setOption('tabSize', node.attrs.tabSize)
    }
    if (node.attrs.theme && node.attrs.theme !== this.cm.getOption('theme')) {
      this.cm.setOption('theme', node.attrs.theme)
    }

    const fontFamilyMark = node.content.firstChild?.marks.find(
      (maybeFontFamilyMark) => maybeFontFamilyMark.type === schema.marks.font_family,
    )
    if (fontFamilyMark) {
      this.contentDom.style.fontFamily = fontFamilyMark.attrs.family
    }

    let change = computeChange(this.cm.getValue(), node.textContent)
    if (change) {
      this.updating = true
      this.cm.replaceRange(change.text, this.cm.posFromIndex(change.from), this.cm.posFromIndex(change.to))
      this.updating = false
    }
    return true
  }

  handleLanguageSelection = (language: keyof typeof CODE_LANGUAGES) => {
    const { view, node, getPos } = this
    const pos = getPos()
    const {
      state: { tr },
    } = view
    tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      language,
      mime: CODE_LANGUAGES[language].mime,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      mode: CODE_LANGUAGES[language].mode! ?? null,
    })
    view.dispatch(tr)
    view.focus()
  }

  handleThemeSelection = (theme: (typeof CODE_THEMES)[number]) => {
    const { view, node, getPos } = this
    const pos = getPos()
    const {
      state: { tr },
    } = view
    tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      theme: theme,
    })
    view.dispatch(tr)
    view.focus()
  }

  handleDelete = () => {
    const { view, node, getPos } = this
    const pos = getPos()
    view.dispatch(view.state.tr.delete(pos, pos + node.nodeSize))
    view.focus()
  }

  handleToggleLineNumbers = () => {
    const {
      view: {
        dispatch,
        state: { tr },
      },
      node,
      getPos,
    } = this
    dispatch(tr.setNodeMarkup(getPos(), undefined, { ...node.attrs, lineNumbers: !node.attrs.lineNumbers }))
  }

  stopEvent() {
    return true
  }

  destroy() {
    if (this.hoverContainer && this.hoverContainer.parentNode) {
      this.root.unmount()
    }
  }
}

const computeChange = (oldVal: string, newVal: string) => {
  if (oldVal === newVal) return null
  let start = 0,
    oldEnd = oldVal.length,
    newEnd = newVal.length
  while (start < oldEnd && oldVal.charCodeAt(start) === newVal.charCodeAt(start)) ++start
  while (oldEnd > start && newEnd > start && oldVal.charCodeAt(oldEnd - 1) === newVal.charCodeAt(newEnd - 1)) {
    oldEnd--
    newEnd--
  }
  return { from: start, to: oldEnd, text: newVal.slice(start, newEnd) }
}
